"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import styled from "styled-components";
import DatePicker, { registerLocale } from "react-datepicker";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from '@app/store';
import { fetchProducts } from '@features/Element/productSlice';
import { IProduct } from '@features/Element/types';
import { Search } from "lucide-react";
import moment from "moment";

// Registra o locale pt-BR para o DatePicker
registerLocale("pt-BR", ptBR);

export const FilterSection = ({
  totalActivities,
  totalPriorityActivities,
  totalDelayedActivities,
  totalCompletedActivities,
  totalDoneToday,
  totalDoneWeek,
  totalDoneMonth,
  toBeDoneToday,
  toBeDoneWeek,
  toBeDoneMonth,
  onDateChange,
  onProductChange,
  onSearch,
  onFilterChange,
  selectedTime,
  setSelectedTime,
  stats,
  activeFilter = 'total',
}: {
  totalActivities: number;
  totalPriorityActivities: number;
  totalDelayedActivities: number;
  totalCompletedActivities: number;
  totalDoneToday: number;
  totalDoneWeek: number;
  totalDoneMonth: number;
  toBeDoneToday: number;
  toBeDoneWeek: number;
  toBeDoneMonth: number;
  selectedTime:string,
  setSelectedTime:string,
  onDateChange?: (startDate: Date | null, endDate: Date | null) => void;
  onProductChange?: (productId: string | null) => void;
  onSearch?: (searchTerm: string) => void;
  onFilterChange?: (filter: 'total' | 'priority' | 'delayed' | 'completed') => void;
  activeFilter?: 'total' | 'priority' | 'delayed' | 'completed';
}) => {
  // Estados para datepicker
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  
  // Estados para filtro de produtos
  const [productOpen, setProductOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
  
  const startDateRef = React.useRef<HTMLDivElement>(null);
  const endDateRef = React.useRef<HTMLDivElement>(null);
  const productFilterRef = React.useRef<HTMLDivElement>(null);
  
  // Redux para buscar produtos
  const dispatch = useDispatch<AppDispatch>();
  const { products, loading: productsLoading } = useSelector((state: RootState) => state.product);

  const [searchTerm, setSearchTerm] = useState("");

  // Buscar produtos quando o componente montar
  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  const handleStartDateChange = (date: Date | Date[] | null) => {
    // Garantir que estamos trabalhando com uma única data
    const singleDate = Array.isArray(date) ? date[0] : date;
    setStartDate(singleDate);
    setStartDateOpen(false);
    let end_date = Array.isArray(date) ? date[1] : endDate;
    setEndDate(end_date);

    console.log('end', end_date)
    if (onDateChange) {
      onDateChange(singleDate, end_date);
    }
  };

  const handleEndDateChange = (date: Date | Date[] | null) => {
    // Garantir que estamos trabalhando com uma única data
    const singleDate = Array.isArray(date) ? date[0] : date;
    setEndDate(singleDate);
    setEndDateOpen(false);
    if (onDateChange) {
      onDateChange(startDate, singleDate);
    }
  };
  
  const handleProductSelect = (product: IProduct) => {
    setSelectedProduct(product);
    setProductOpen(false);
    if (onProductChange) {
      onProductChange(product._id || null);
    }
  };

  // Fechar os datepickers e a lista de produtos ao clicar fora deles
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (startDateOpen && startDateRef.current && !startDateRef.current.contains(event.target as Node)) {
        setStartDateOpen(false);
      }
      if (endDateOpen && endDateRef.current && !endDateRef.current.contains(event.target as Node)) {
        setEndDateOpen(false);
      }
      if (productOpen && productFilterRef.current && !productFilterRef.current.contains(event.target as Node)) {
        setProductOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [startDateOpen, endDateOpen, productOpen]);

  const formatDate = (date: Date | null) => {
    if (!date) return "Data inicial";
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  };

  const formatEndDate = (date: Date | null) => {
    if (!date) return "Data Final";
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  };

  // Função para lidar com a pesquisa
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = () => {
    if (onSearch) {
      onSearch(searchTerm);
    }
  };

  // Função para lidar com a tecla Enter no campo de pesquisa
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  // Função para lidar com o clique nos botões de filtro
  const handleFilterClick = (filter: 'total' | 'priority' | 'delayed' | 'completed') => {
    if (onFilterChange) {
      onFilterChange(filter);
    }
  };

  return (
    <FilterContainer>
      <Header>
        <div className=" justify-between items-center mb-2">

        <h1 className="text-2xl font-bold">Atividades</h1>
        {/* {subtitle && <p style={{color:'grey'}} className="mt-1">{subtitle}</p>} */}
      </div>
      
        <FilterButtons>
          <ButtonGroup>
            <FilterButton 
              active={activeFilter === 'total'} 
              onClick={() => handleFilterClick('total')}
            >
              Total 
              {/* ({totalActivities}) */}
            </FilterButton>
            {/* <FilterButton 
              active={activeFilter === 'priority'} 
              onClick={() => handleFilterClick('priority')}
            >
              Prioridade ({totalPriorityActivities})
            </FilterButton>
            <FilterButton 
              active={activeFilter === 'delayed'} 
              onClick={() => handleFilterClick('delayed')}
            >
              Atrasadas ({totalDelayedActivities})
            </FilterButton>
            <FilterButton 
              active={activeFilter === 'completed'} 
              onClick={() => handleFilterClick('completed')}
            >
              Concluídos ({totalCompletedActivities})
            </FilterButton> */}
          </ButtonGroup>
        </FilterButtons>
      </Header>

      <FiltersWrapper style={{boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px'}}>
        <TimeFilter>
          <TimeLabel className="hover:bg-gray-50" style={{cursor:'pointer'}}>
            <span>Tempo</span>
            <TimeIcon
              src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/77a5530635e35463f0b9cc4287ec3142ef3ebd04?placeholderIfAbsent=true"
              alt="Time icon"
            />
          </TimeLabel>
          <TimePeriod>
            <div onClick={(e)=>{
              handleStartDateChange([new Date(),null])

            }} style={{paddingLeft:10,paddingRight:10,fontSize:16,cursor:'pointer'}} className="hover:bg-gray-50">Hoje </div> | 
             <div 
             onClick={(e)=>{
              handleStartDateChange([moment().startOf('week').toDate(),moment().endOf('week').toDate()])
            }}
             style={{paddingLeft:10,paddingRight:10,fontSize:16,cursor:'pointer'}} className="hover:bg-gray-50">Essa Semana </div> | 
            <div  onClick={(e)=>{
              handleStartDateChange([moment().startOf('month').toDate(),moment().endOf('month').toDate()])
            }} style={{paddingLeft:10,paddingRight:10,fontSize:16,cursor:'pointer'}} className="hover:bg-gray-50">Esse mês </div>
          </TimePeriod>
        </TimeFilter>
        
        <DateFilters>
          <DatePickerWrapper ref={startDateRef}>
            <DateButton 
              hasValue={!!startDate}
              onClick={(e) => {
                e.stopPropagation();
                setStartDateOpen(!startDateOpen);
                if (startDateOpen) return;
                setEndDateOpen(false);
                setProductOpen(false);
              }}
            >
              <CalendarIcon
                src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/4c0ef7d40ddc1d826b09dfa9c5810769fa8f4b39?placeholderIfAbsent=true"
                alt="Calendar"
              />
              <span>{formatDate(startDate)}</span>
            </DateButton>
            {startDateOpen && (
              <DatePickerContainer>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => handleStartDateChange(date)}
                  locale="pt-BR"
                  dateFormat="dd/MM/yyyy"
                  inline
                  maxDate={endDate || undefined}
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  calendarClassName="custom-datepicker"
                  selectsStart
                  startDate={startDate || undefined}
                  endDate={endDate || undefined}
                />
              </DatePickerContainer>
            )}
          </DatePickerWrapper>
          <DatePickerWrapper ref={endDateRef}>
            <DateButton 
              hasValue={!!endDate}
              onClick={(e) => {
                e.stopPropagation();
                setEndDateOpen(!endDateOpen);
                if (endDateOpen) return;
                setStartDateOpen(false);
                setProductOpen(false);
              }}
            >
              <CalendarIcon
                src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/4c0ef7d40ddc1d826b09dfa9c5810769fa8f4b39?placeholderIfAbsent=true"
                alt="Calendar"
              />
              <span>{formatEndDate(endDate)}</span>
            </DateButton>
            {endDateOpen && (
              <DatePickerContainer>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => handleEndDateChange(date)}
                  locale="pt-BR"
                  dateFormat="dd/MM/yyyy"
                  inline
                  minDate={startDate || undefined}
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  calendarClassName="custom-datepicker"
                  selectsEnd
                  startDate={startDate || undefined}
                  endDate={endDate || undefined}
                />
              </DatePickerContainer>
            )}
          </DatePickerWrapper>
        </DateFilters>
      </FiltersWrapper>

      <SearchBar>
        {/* <SearchWrapper>
          <SearchInput 
            placeholder="Pesquise aqui..." 
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
          />
        </SearchWrapper> */}
         <div className="flex w-full" style={{ position: "relative",boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px' }}>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}

                    placeholder="Pesquisar"
                    className="p-2 border rounded"
                    style={{
                      width: "-webkit-fill-available",
                      height:50,
                      // marginRight: 20,
                      borderRadius: 5,
                      paddingLeft: 40,
                    }}
                  />
                  <Search
                    style={{
                      position: "absolute",
                      top: "20%",
                      left: 10,
                      color: "grey",
                    }}
                  />
                  </div>
        <ProductFilterWrapper ref={productFilterRef}>
          <ProductFilter 
            onClick={() => setProductOpen(!productOpen)}
            hasValue={!!selectedProduct}
          >
            <span>{selectedProduct ? selectedProduct.name : "Todos os produtos"}</span>
            <FilterIcon
              src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/7004eebf6bab7db8bd5a948f7a61fc1a1113d717?placeholderIfAbsent=true"
              alt="Filter"
            />
          </ProductFilter>
          {productOpen && (
            <ProductDropdown>
              <ProductItem 
                key="all"
                onClick={() => {
                  setSelectedProduct(null);
                  setProductOpen(false);
                  if (onProductChange) {
                    onProductChange(null);
                  }
                }}
              >
                Todos os produtos
              </ProductItem>
              {productsLoading ? (
                <ProductItem>Carregando...</ProductItem>
              ) : (
                products.map((product) => (
                  <ProductItem 
                    key={product._id} 
                    onClick={() => handleProductSelect(product)}
                    active={selectedProduct?._id === product._id}
                  >
                    {product.name}
                  </ProductItem>
                ))
              )}
            </ProductDropdown>
          )}
        </ProductFilterWrapper>
      </SearchBar>
    </FilterContainer>
  );
};

const FilterContainer = styled.section`
  width: 100%;
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 15px;
  font-family:
    Inter,
    -apple-system,
    Roboto,
    Helvetica,
    sans-serif;
`;

const Title = styled.h2`
  font-size: 24px;
  color: #272b30;
  font-weight: 600;
  margin: 0;
`;

const FilterButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 40px 138px;
  font-size: 14px;
  color: #3a3f51;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.active ? "#1777cf" : "#d8e0f0")};
  background-color: #fcfcfc;
  color: ${(props) => (props.active ? "#1777cf" : "#3a3f51")};
  font-family:
    Inter,
    -apple-system,
    Roboto,
    Helvetica,
    sans-serif;
  font-size: 14px;
  cursor: pointer;
  box-shadow:
    0px 1px 1px 0px rgba(0, 0, 0, 0.12),
    0px 2px 5px 0px rgba(103, 110, 118, 0.08);
`;

const FiltersWrapper = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 16px 30px;
  margin-top: 46px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
`;

const TimeFilter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  color: #7d8592;
  font-family:
    Inter,
    -apple-system,
    Roboto,
    Helvetica,
    sans-serif;
  flex: 1;
`;

const TimeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TimeIcon = styled.img`
  width: 12px;
  aspect-ratio: 1;
  object-fit: contain;
`;

const TimePeriod = styled.div`
  min-width: 400px;
  display:flex;
`;

const SearchFilter = styled.div`
  flex: 1;
  min-width: 200px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 18px;
  border-radius: 8px;
  border: 0.25px solid #d8e0f0;
  background-color: #f4f4f4;
  font-family:
    Inter,
    -apple-system,
    Roboto,
    Helvetica,
    sans-serif;
  font-size: 14px;
  color: #7d8592;
  outline: none;

  &::placeholder {
    color: #7d8592;
  }
`;

const DateFilters = styled.div`
  display: flex;
  gap: 10px;
`;

const DatePickerWrapper = styled.div`
  position: relative;
`;

const DateButton = styled.button<{ hasValue?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px 20px;
  border-radius: 8px;
  border: 0.5px solid ${props => props.hasValue ? "#1777cf" : "#d8e0f0"};
  background-color: ${props => props.hasValue ? "#f2f8fe" : "#fcfcfc"};
  color: ${props => props.hasValue ? "#1777cf" : "#7d8592"};
  font-family:
    Inter,
    -apple-system,
    Roboto,
    Helvetica,
    sans-serif;
  font-size: 14px;
  cursor: pointer;
  box-shadow:
    0px 1px 1px 0px rgba(0, 0, 0, 0.12),
    0px 2px 5px 0px rgba(103, 110, 118, 0.08);
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${props => props.hasValue ? "#e6f2ff" : "#f4f4f4"};
  }
`;

const CalendarIcon = styled.img`
  width: 12px;
  aspect-ratio: 1;
  object-fit: contain;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 25px;
  margin-top: 26px;
  align-items: center;
  @media (max-width: 991px) {
    flex-wrap: wrap;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  min-width: 240px;
  background-color: #fcfcfc;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
`;

const ProductFilterWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 46px;
`;

const ProductFilter = styled.button<{ hasValue?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0px;
  height:50px;
  padding: 10px 20px;
  background-color: ${props => props.hasValue ? "#f2f8fe" : "#fcfcfc"};
  border: none;
  border-radius: 8px;
  box-shadow:
    0px 1px 1px 0px rgba(0, 0, 0, 0.12),
    0px 2px 5px 0px rgba(103, 110, 118, 0.08);
  color: ${props => props.hasValue ? "#1777cf" : "#7d8592"};
  font-family:
    Inter,
    -apple-system,
    Roboto,
    Helvetica,
    sans-serif;
  font-size: 14px;
  cursor: pointer;
  width: 207px;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${props => props.hasValue ? "#e6f2ff" : "#f4f4f4"};
  }
`;

const ProductDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 250px;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 5px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  padding: 8px 0;
`;

const ProductItem = styled.div<{ active?: boolean }>`
  padding: 10px 16px;
  font-family: Inter, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  color: ${props => props.active ? "#1777cf" : "#3a3f51"};
  background-color: ${props => props.active ? "#f2f8fe" : "transparent"};
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? "#e6f2ff" : "#f8f9fa"};
  }
`;

const FilterIcon = styled.img`
  width: 12px;
  aspect-ratio: 2/1;
  object-fit: contain;
`;

const DatePickerContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 10;
  margin-top: 5px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background-color: #ffffff;
  
  .custom-datepicker {
    font-family: Inter, -apple-system, Roboto, Helvetica, sans-serif;
    border-radius: 8px;
    border: 1px solid #d8e0f0;
    background-color: #ffffff;
  }
  
  /* Estilos globais para o datepicker */
  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    display: block;
    width: 100%;
  }
  
  .react-datepicker {
    font-family: Inter, -apple-system, Roboto, Helvetica, sans-serif;
    border-radius: 8px;
    border: 1px solid #d8e0f0;
  }
  
  .react-datepicker__header {
    background-color: #fcfcfc;
    border-bottom: 1px solid #d8e0f0;
    padding-top: 10px;
  }
  
  .react-datepicker__day--selected {
    background-color: #1777cf;
    border-radius: 50%;
  }
  
  .react-datepicker__day:hover {
    background-color: #e6f2ff;
    border-radius: 50%;
  }
  
  .react-datepicker__day-name, .react-datepicker__day {
    margin: 0.2rem;
    width: 1.9rem;
    height: 1.9rem;
    line-height: 1.9rem;
    text-align: center;
  }
  
  .react-datepicker__navigation {
    top: 13px;
  }
  
  .react-datepicker__month-container {
    float: none;
  }
  
  .react-datepicker__current-month {
    font-weight: 500;
    font-size: 16px;
    color: #3a3f51;
    margin-bottom: 10px;
  }
  
  .react-datepicker__day-name {
    color: #7d8592;
    font-weight: 500;
  }
  
  .react-datepicker__day--outside-month {
    color: #c5c9d6;
  }
  
  .react-datepicker__day--disabled {
    color: #c5c9d6;
    cursor: default;
  }
`;

